import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { getServices } from "../lib/servicesCache";
import { useSettings } from "../context/SettingsContext";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Star, Scissors, ArrowUpRight, Sparkles, Quote, ShoppingBag, MapPin, Mail, Phone, Send, Clock, Flame, Copy } from "lucide-react";
import { VideoTrigger } from "../components/VideoPlayerModal";
import { CategoryGrid } from "../components/CategoryGrid";
import { CategoryChipBar } from "../components/CategoryChipBar";
import ServicesSlider from "../components/ServicesSlider";
import { sortCategoriesByOrder } from "../lib/categoryOrder";
import { FloralCorner } from "../components/FloralCorner";
import { ServiceBubbles } from "../components/ServiceBubbles";
import { siteImageUrl } from "../components/SiteImageSetting";

const HERO = "/hero_girl.jpg";
export const INTERIOR = "https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6?auto=format&fit=crop&w=1200&q=80";

const HeroPhoto = () => (
    <div className="absolute inset-y-0 right-0 w-[72%] md:w-1/2" data-testid="hero-photo">
        <img src={HERO} alt="Relaxed guest enjoying a Tresses beauty ritual" className="w-full h-full object-cover object-[center_25%]" fetchpriority="high" data-testid="hero-photo-img" />
        <div className="absolute inset-y-0 left-0 w-2/5 bg-gradient-to-r from-[#FBF6F0] via-[#FBF6F0]/60 to-transparent" aria-hidden />
        <div className="md:hidden absolute inset-0 bg-gradient-to-r from-[#FBF6F0]/85 via-[#FBF6F0]/35 to-transparent" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-[#FBF6F0]/70 to-transparent" aria-hidden />
        <div className="hidden md:block absolute bottom-28 right-8 lg:right-16 bg-[#FFFAF6]/90 backdrop-blur-sm border border-[#C29A5B]/50 px-6 py-4 shadow-[0_12px_30px_-14px_rgba(107,21,53,0.45)]" data-testid="hero-photo-badge">
            <div className="font-serif-display italic text-2xl lg:text-3xl text-[#6B1535] leading-none">Look good,</div>
            <div className="font-serif-display italic text-2xl lg:text-3xl gold-text leading-tight">feel better.</div>
        </div>
    </div>
);

export default function LandingPage() {
    const { settings } = useSettings();
    // helper: default-true for section visibility so unset legacy settings still render
    const on = (key) => settings[key] !== false;
    const { addItem } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [services, setServices] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [bestSellers, setBestSellers] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [team, setTeam] = useState([]);
    const [products, setProducts] = useState([]);

    useEffect(() => {
        Promise.all([
            getServices().then((data) => setServices(data)),
            api.get("/promotions").then((r) => setPromotions(r.data)),
            api.get("/services/best-sellers").then((r) => setBestSellers(r.data)),
            api.get("/testimonials").then((r) => setTestimonials(r.data)),
            api.get("/reviews").then((r) => setReviews(r.data)),
            api.get("/team").then((r) => setTeam(r.data)),
            api.get("/products", { params: { active_only: true } }).then((r) => setProducts(r.data)),
        ]).catch(() => {});
    }, []);

    return (
        <div className="bg-[#FBF6F0]" data-testid="landing-page">
            {/* HERO */}
            {on("hero_section_enabled") && (
            <section className="relative min-h-screen flex items-center grain overflow-hidden bg-[radial-gradient(ellipse_at_75%_40%,#F9DDE3_0%,#FBF6F0_55%)]" data-testid="hero-section">
                <HeroPhoto />
                {/* Floral accents — decorative only, non-interactive */}
                <FloralCorner className="absolute -top-6 -left-6 hidden 2xl:block" rotate={0} size={300} opacity={0.75} data-testid="hero-floral-top-left" />
                <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-12 pt-32 pb-20 grid md:grid-cols-2 gap-14 items-center">
                    <div className="max-w-2xl fade-up">
                        <div className="text-overline">{settings.hero_overline || "Unisex Salon · Est. 2018"}</div>
                        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-[#3D0C1F] mt-6 leading-[0.95]">
                            {settings.hero_title_line1 || "Where every"}<br />
                            <span className="gold-text italic">{settings.hero_title_italic || "strand"}</span> {settings.hero_title_line3?.split(" ")[0] || "tells"}<br />
                            {(settings.hero_title_line3 || "tells a story.").split(" ").slice(1).join(" ") || "a story."}
                        </h1>
                        <p className="text-base sm:text-lg text-[#7A5262] mt-8 max-w-lg leading-relaxed">
                            {settings.hero_subtitle || "Editorial cuts. Bespoke colour. Skin rituals you'll want to bookmark. Tresses is a sanctuary for those who treat self-care as ceremony."}
                        </p>
                        <div className="flex flex-wrap gap-4 mt-10">
                            <Link to="/book" className="btn-gold" data-testid="hero-book-btn">
                                Book Your Slot <ArrowUpRight size={14} />
                            </Link>
                            <Link to="/services" className="btn-outline-gold" data-testid="hero-services-btn">
                                Explore Services
                            </Link>
                        </div>
                        {on("hero_stats_enabled") && (
                        <div className="flex items-center gap-6 mt-14 pt-8 border-t border-[#6B1535]/15">
                            <div>
                                <div className="font-serif-display text-3xl gold-text">{settings.stat_rating || "4.9"}</div>
                                <div className="text-[0.65rem] tracking-[0.2em] text-[#8A6574] uppercase">Google Rating</div>
                            </div>
                            <div className="h-10 w-px bg-[#6B1535]/10" />
                            <div>
                                <div className="font-serif-display text-3xl gold-text">{settings.stat_guests || "12k+"}</div>
                                <div className="text-[0.65rem] tracking-[0.2em] text-[#8A6574] uppercase">Happy Guests</div>
                            </div>
                            <div className="h-10 w-px bg-[#6B1535]/10" />
                            <div>
                                <div className="font-serif-display text-3xl gold-text">{settings.stat_stylists || "7"}</div>
                                <div className="text-[0.65rem] tracking-[0.2em] text-[#8A6574] uppercase">Master Stylists</div>
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            </section>
            )}

            <ServiceBubbles />

            {/* Membership CTA — logged-in users only */}
            {user && (
                <section className="border-t border-[#6B1535]/10 bg-gradient-to-br from-[#F7E4DE] to-[#FBF6F0] py-14 grain" data-testid="membership-cta-strip">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-start md:items-center gap-8 justify-between">
                        <div className="flex-1">
                            <div className="text-overline">Members save more</div>
                            <h3 className="font-serif-display text-2xl sm:text-3xl text-[#3D0C1F] mt-3 leading-tight">
                                Prepay for salon credit — get <span className="italic gold-text">bonus points</span> on every top-up.
                            </h3>
                            <p className="text-[#7A5262] mt-3 text-sm max-w-xl">
                                Your current balance:
                                {" "}
                                <span className="text-[#8F622C] font-bold" data-testid="landing-credit-balance">S${Number(user?.credit_balance || 0).toFixed(2)}</span>
                                . Apply credits at checkout on any booking.
                            </p>
                        </div>
                        <Link to="/membership" className="btn-gold shrink-0" data-testid="landing-membership-cta">
                            View Membership Plans <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </section>
            )}

            {/* SERVICES */}
            {on("services_section_enabled") && (
            <section className="py-32 border-t border-[#6B1535]/10" id="services" data-testid="services-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
                        <div>
                            <div className="text-overline">{settings.services_overline || "Menu"}</div>
                            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3D0C1F] mt-3">{settings.services_heading || "Our Services"}</h2>
                            {settings.services_subtitle && <p className="text-[#7A5262] mt-3 max-w-xl">{settings.services_subtitle}</p>}
                        </div>
                        <Link to="/services" className="btn-outline-gold" data-testid="services-view-all">
                            View Full Menu <ArrowUpRight size={14} />
                        </Link>
                    </div>

                    <ServicesSlider
                        services={services}
                        categoryOrder={settings.category_order}
                        showDuration={settings.show_service_duration !== false}
                        title={settings.services_slider_overline || "Signature picks"}
                        subtitle={settings.services_slider_heading || "One signature service from every category."}
                    />

                    {/* Quick category navigator — chips navigate to /services filtered by category */}
                    {services.length > 0 && (() => {
                        const groupedHome = services.reduce((acc, s) => {
                            (acc[s.category] = acc[s.category] || []).push(s);
                            return acc;
                        }, {});
                        const orderedHome = sortCategoriesByOrder(Object.entries(groupedHome), settings.category_order);
                        const chipDefs = [
                            { value: "All", label: "Browse all", count: services.length, accent: true },
                            ...orderedHome.map(([cat, items]) => ({ value: cat, label: cat, count: items.length })),
                        ];
                        return (
                            <div className="mt-10">
                                <CategoryChipBar
                                    chips={chipDefs.map((c) => ({ ...c, onClick: () => navigate(c.value === "All" ? "/services" : `/services?cat=${encodeURIComponent(c.value)}`) }))}
                                    active={null}
                                    onSelect={() => {}}
                                    testIdPrefix="home-chip"
                                    barTestId="home-category-chip-bar"
                                    sticky={false}
                                />
                            </div>
                        );
                    })()}
                </div>
            </section>
            )}

            {/* BEST SELLERS MARQUEE */}
            {on("best_sellers_section_enabled") && bestSellers.length > 0 && (
                <section className="py-20 border-t border-[#6B1535]/10 overflow-hidden" data-testid="best-sellers-section">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-10">
                        <div className="text-overline">{settings.best_sellers_overline || "This Month's Most Loved"}</div>
                        <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3D0C1F] mt-2">{settings.best_sellers_heading || "Best Sellers"}</h2>
                    </div>
                    <div className="relative">
                        <div className="flex marquee gap-8 w-max">
                            {[...bestSellers, ...bestSellers, ...bestSellers].map((s, i) => (
                                <div key={`${s.id}-${i}`} className="flex items-center gap-6 pr-8 border-r border-[#6B1535]/10">
                                    <Scissors size={18} className="text-[#8F622C]" />
                                    <span className="font-serif-display text-3xl text-[#3D0C1F] whitespace-nowrap">{s.name}</span>
                                    <span className="text-overline whitespace-nowrap">S${s.price}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* PROMOTIONS */}
            {on("promotions_section_enabled") && (
            <section className="py-32 border-t border-[#6B1535]/10" id="promotions" data-testid="promotions-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="flex items-end justify-between mb-16 gap-6 flex-wrap">
                        <div>
                            <div className="text-overline">Now On</div>
                            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3D0C1F] mt-3">Promotions</h2>
                        </div>
                        <Link to="/promotions" className="btn-outline-gold" data-testid="promotions-view-all">
                            View All Promotions <ArrowUpRight size={14} />
                        </Link>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {promotions.slice(0, 4).map((p) => (
                            <PromoCard key={p.id} promo={p} />
                        ))}
                    </div>
                </div>
            </section>
            )}

            {/* ABOUT STRIP */}
            {on("about_section_enabled") && (
            <section className="py-32 border-t border-[#6B1535]/10" id="about" data-testid="about-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-5">
                        <img src={siteImageUrl(settings.philosophy_image_path, settings.about_image || INTERIOR)} alt="Inside The Tresses salon" decoding="async" className="w-full h-[500px] object-cover" data-testid="philosophy-image" />
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <div className="text-overline" data-testid="about-overline">{settings.about_overline || "The Philosophy"}</div>
                        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3D0C1F] mt-4 leading-tight" data-testid="about-heading">
                            {(() => {
                                const heading = settings.about_heading || "Crafted with intention. Felt for days.";
                                const accent = (settings.about_heading_accent || "").trim();
                                if (!accent) return heading;
                                const idx = heading.toLowerCase().indexOf(accent.toLowerCase());
                                if (idx === -1) return heading;
                                return (
                                    <>
                                        {heading.slice(0, idx)}
                                        <span className="italic gold-text">{heading.slice(idx, idx + accent.length)}</span>
                                        {heading.slice(idx + accent.length)}
                                    </>
                                );
                            })()}
                        </h2>
                        <p className="text-[#7A5262] mt-6 leading-relaxed" data-testid="about-body">
                            {settings.about_body || "We believe a salon visit should be a quiet luxury — a pause in your week where the lighting is right, the conversation is unhurried, and every snip serves a purpose. Whether it's a sharp men's cut or a hand-painted balayage, we obsess over the details so you don't have to."}
                        </p>
                        <div className="grid grid-cols-2 gap-6 mt-10">
                            {[
                                settings.about_pill_1 || "Award-winning team",
                                settings.about_pill_2 || "Premium global products",
                                settings.about_pill_3 || "Hygiene-first studio",
                                settings.about_pill_4 || "Personalised consults",
                            ].filter(Boolean).map((p, i) => (
                                <div key={i} className="flex items-center gap-3 text-[#3D0C1F] text-sm" data-testid={`about-pill-${i + 1}`}>
                                    <Sparkles size={14} className="text-[#8F622C]" /> {p}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
            )}

            {/* BEST SELLERS — moved above Promotions */}

            {/* PROMOTIONS — moved above to right after Services */}

            {/* TEAM */}
            {on("team_section_enabled") && team.length > 0 && (
                <section className="py-32 border-t border-[#6B1535]/10" id="team" data-testid="team-section">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12">
                        <div className="text-overline">The People</div>
                        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3D0C1F] mt-3">Meet the team behind <span className="italic gold-text">Tresses</span>.</h2>
                        <p className="text-[#7A5262] mt-4 max-w-xl">Led by award-winning artists, our team is the heartbeat of every visit.</p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
                            {team.map((m) => (
                                <div key={m.id} className="card-luxe grain group" data-testid={`team-${m.id}`}>
                                    {m.photo && (
                                        <div className="aspect-[4/5] overflow-hidden">
                                            <img src={m.photo} alt={m.name} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
                                        </div>
                                    )}
                                    <div className="p-6">
                                        <h3 className="font-serif-display text-2xl text-[#3D0C1F]">{m.name}</h3>
                                        <div className="text-overline mt-1">{m.role}</div>
                                        {m.bio && <p className="text-sm text-[#7A5262] mt-4 leading-relaxed">{m.bio}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* PRODUCTS / SHOP */}
            {on("products_section_enabled") && settings.products_enabled !== false && products.length > 0 && (
                <section className="py-32 border-t border-[#6B1535]/10 bg-[#F7E4DE]" id="products" data-testid="products-section">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12">
                        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
                            <div>
                                <div className="text-overline">Take Tresses Home</div>
                                <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3D0C1F] mt-3">Shop the <span className="italic gold-text">ritual</span>.</h2>
                                <p className="text-[#7A5262] mt-3 max-w-md">Salon-grade tools and rituals — curated by our master stylists.</p>
                            </div>
                            <Link to="/shop" className="btn-outline-gold" data-testid="products-view-all">
                                View Full Shop <ArrowUpRight size={14} />
                            </Link>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {products.slice(0, 4).map((p) => (
                                <div key={p.id} className="card-luxe grain group flex flex-col" data-testid={`landing-product-${p.id}`}>
                                    {p.image && <div className="aspect-square overflow-hidden">
                                        <img src={p.image} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    </div>}
                                    <div className="p-5 flex-1 flex flex-col">
                                        <div className="text-overline">{p.category}</div>
                                        <h3 className="font-serif-display text-xl text-[#3D0C1F] mt-2 leading-tight flex-1">{p.name}</h3>
                                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#6B1535]/10">
                                            <span className="font-serif-display text-2xl gold-text">S${p.price}</span>
                                            <button onClick={() => addItem(p)} disabled={p.stock <= 0}
                                                className="text-[#8F622C] hover:text-[#3D0C1F] disabled:opacity-40 disabled:cursor-not-allowed text-xs uppercase tracking-[0.2em] flex items-center gap-1"
                                                data-testid={`add-cart-landing-${p.id}`}>
                                                <ShoppingBag size={12} /> Add
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* TESTIMONIALS */}
            {on("testimonials_section_enabled") && (
            <section className="py-32 border-t border-[#6B1535]/10" data-testid="testimonials-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-overline">Voices</div>
                    <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3D0C1F] mt-3 mb-16">In their words</h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {testimonials.map((t) => (
                            <div key={t.id} className="card-luxe p-8 grain" data-testid={`testimonial-${t.id}`}>
                                <Quote size={28} className="text-[#8F622C]" />
                                <p className="font-serif-display text-xl text-[#3D0C1F] mt-4 leading-relaxed italic">“{t.quote}”</p>
                                <div className="flex items-center gap-3 mt-8 pt-6 border-t border-[#6B1535]/10">
                                    {t.avatar && <img src={t.avatar} alt={t.name} loading="lazy" decoding="async" className="w-10 h-10 rounded-full object-cover" />}
                                    <div>
                                        <div className="text-sm text-[#3D0C1F]">{t.name}</div>
                                        <div className="text-xs text-[#8A6574]">{t.role}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            )}

            {/* GOOGLE REVIEWS */}
            {on("reviews_section_enabled") && (
            <section className="py-32 border-t border-[#6B1535]/10 bg-[#F7E4DE]" data-testid="reviews-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
                        <div>
                            <div className="text-overline">From Google</div>
                            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3D0C1F] mt-3">Reviews · 4.9 ★</h2>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {reviews.map((r) => (
                            <div key={r.id} className="card-luxe p-6" data-testid={`review-${r.id}`}>
                                <div className="flex items-center gap-1 mb-3">
                                    {[...Array(r.rating)].map((_, i) => <Star key={i} size={14} className="text-[#8F622C] fill-[#C29A5B]" />)}
                                </div>
                                <p className="text-sm text-[#7A5262] leading-relaxed">{r.text}</p>
                                <div className="flex justify-between mt-6 pt-4 border-t border-[#6B1535]/10">
                                    <span className="text-xs text-[#3D0C1F]">{r.author}</span>
                                    <span className="text-xs text-[#8A6574]">{r.date}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            )}

            {/* FRANCHISE */}
            {settings.franchise_enabled !== false && (
                <FranchiseSection settings={settings} />
            )}

            {/* CTA */}
            {on("cta_section_enabled") && (
            <section className="py-32 border-t border-[#6B1535]/10 grain" data-testid="cta-section">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="font-serif-display text-4xl sm:text-6xl text-[#3D0C1F]">
                        {settings.cta_heading || "Ready to feel extraordinary?"}
                    </h2>
                    <p className="text-[#7A5262] mt-6 max-w-xl mx-auto">{settings.cta_subtitle || "Book a slot, sip a complimentary chai, and let our team craft your next look."}</p>
                    <Link to="/book" className="btn-gold mt-10 inline-flex" data-testid="cta-book-btn">{settings.cta_button_text || "Book Your Slot"}</Link>
                </div>
            </section>
            )}
        </div>
    );
}

function FranchiseSection({ settings }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/franchise-inquiries", form);
            setDone(true);
            setForm({ name: "", email: "", phone: "", city: "", message: "" });
            toast.success("Inquiry sent — our team will be in touch soon");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Could not send inquiry"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="py-32 border-t border-[#6B1535]/10 bg-[#F7E4DE] grain" id="franchise" data-testid="franchise-section">
            <div className="max-w-6xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-start">
                <div>
                    <div className="text-overline">{settings.franchise_overline || "Own a Tresses"}</div>
                    <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3D0C1F] mt-3 leading-[1.05]">
                        {settings.franchise_heading || "Bring Tresses to your city."}
                    </h2>
                    <p className="text-[#7A5262] mt-6 leading-relaxed max-w-md">{settings.franchise_subtitle || "We're partnering with passionate entrepreneurs to open Tresses studios across Asia."}</p>

                    <div className="mt-10 space-y-4">
                        <div className="flex items-start gap-3">
                            <Sparkles size={16} className="text-[#8F622C] mt-1 flex-shrink-0" />
                            <div>
                                <div className="text-sm text-[#3D0C1F]">{settings.franchise_benefit_1_title || "Proven luxury concept"}</div>
                                <div className="text-xs text-[#8A6574]">{settings.franchise_benefit_1_desc || "Elegant ivory & gold sanctuary aesthetic that guests love"}</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Sparkles size={16} className="text-[#8F622C] mt-1 flex-shrink-0" />
                            <div>
                                <div className="text-sm text-[#3D0C1F]">{settings.franchise_benefit_2_title || "End-to-end support"}</div>
                                <div className="text-xs text-[#8A6574]">{settings.franchise_benefit_2_desc || "Training, supply chain, marketing & operations playbook"}</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Sparkles size={16} className="text-[#8F622C] mt-1 flex-shrink-0" />
                            <div>
                                <div className="text-sm text-[#3D0C1F]">{settings.franchise_benefit_3_title || "Digital-first platform"}</div>
                                <div className="text-xs text-[#8A6574]">{settings.franchise_benefit_3_desc || "Online booking, loyalty, and customer dashboard built-in"}</div>
                            </div>
                        </div>
                    </div>

                    {settings.franchise_email && (
                        <div className="mt-10 pt-6 border-t border-[#6B1535]/15 flex items-center gap-2 text-sm text-[#7A5262]">
                            <Mail size={14} className="text-[#8F622C]" />
                            Prefer email? <a href={`mailto:${settings.franchise_email}`} className="text-[#8F622C] hover:text-[#3D0C1F]">{settings.franchise_email}</a>
                        </div>
                    )}
                </div>

                <div className="card-luxe p-8 grain">
                    {done ? (
                        <div className="text-center py-12" data-testid="franchise-success">
                            <Sparkles size={28} className="text-[#8F622C] mx-auto" />
                            <h3 className="font-serif-display text-3xl text-[#3D0C1F] mt-4">Thank you.</h3>
                            <p className="text-[#7A5262] mt-3 text-sm">Your inquiry has been received. Our franchise team will reach out within 2-3 business days.</p>
                            <button onClick={() => setDone(false)} className="btn-outline-gold mt-8">Send another inquiry</button>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} className="space-y-4" data-testid="franchise-form">
                            <div className="text-overline">Franchise Inquiry</div>
                            <h3 className="font-serif-display text-2xl text-[#3D0C1F]">Let's talk.</h3>
                            <div className="grid grid-cols-2 gap-3 pt-3">
                                <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-3 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none" data-testid="franchise-name" />
                                <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-3 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none" data-testid="franchise-email-input" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <input required type="tel" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-3 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none" data-testid="franchise-phone" />
                                <input required placeholder="City of interest" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-3 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none" data-testid="franchise-city" />
                            </div>
                            <textarea rows={4} placeholder="Tell us about yourself and your franchise interest (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-3 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none" data-testid="franchise-message" />
                            <button type="submit" disabled={submitting} className="btn-gold w-full justify-center disabled:opacity-50" data-testid="franchise-submit">
                                {submitting ? "Sending…" : "Send Inquiry"} <Send size={12} />
                            </button>
                            <p className="text-[0.65rem] text-[#8A6574] text-center pt-2">By submitting, you consent to be contacted by Tresses about your franchise interest.</p>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}


function PromoCard({ promo: p }) {
    const expired = !!p.expires_at && new Date(p.expires_at).getTime() <= Date.now();
    const soldOut = typeof p.seats_left === "number" && p.seats_left <= 0;
    const unavailable = expired || soldOut;
    const navigate = useNavigate();

    const copyCode = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!p.coupon_code) return;
        navigator.clipboard?.writeText(p.coupon_code);
        toast.success(`Code ${p.coupon_code} copied`);
    };

    const goToBook = () => {
        if (unavailable) return;
        if (p.type === "package") {
            navigate(`/book?promo=${p.id}`);
        } else if (p.coupon_code) {
            navigate(`/book?coupon=${encodeURIComponent(p.coupon_code)}`);
        } else {
            navigate("/book");
        }
    };

    return (
        <div
            role={unavailable ? undefined : "button"}
            tabIndex={unavailable ? -1 : 0}
            onClick={unavailable ? undefined : goToBook}
            onKeyDown={unavailable ? undefined : (e) => { if (e.key === "Enter") goToBook(); }}
            className={`card-luxe grain group flex flex-col ${unavailable ? "" : "cursor-pointer"}`}
            data-testid={`promotion-card-${p.id}`}
        >
            {p.image && (
                <div className="aspect-[16/9] overflow-hidden relative">
                    <img src={p.image} alt={p.title} className={`w-full h-full object-cover transition-all duration-700 ${unavailable ? "grayscale opacity-60" : "group-hover:scale-105"}`} />
                    <div className="absolute top-2 right-2 bg-[#6B1535] text-[#FFF8F2] px-2 py-0.5 text-[0.55rem] tracking-[0.2em] uppercase font-semibold">{p.discount_label}</div>
                    {unavailable && (
                        <div className="absolute top-2 left-2 bg-[#FBF6F0]/85 border border-[#6B1535]/15 text-[#3D0C1F] px-2 py-0.5 text-[0.55rem] tracking-[0.2em] uppercase">
                            {soldOut ? "Sold out" : "Ended"}
                        </div>
                    )}
                </div>
            )}
            <div className="p-3 flex-1 flex flex-col">
                <h3 className="font-serif-display text-base leading-tight text-[#3D0C1F]">{p.title}</h3>
                <p className="text-[0.7rem] text-[#8A6574] mt-1 line-clamp-2 leading-snug flex-1">{p.description}</p>
                {p.coupon_code && !unavailable && p.type !== "package" && (
                    <button
                        type="button"
                        onClick={copyCode}
                        className="mt-2 inline-flex items-center gap-1.5 self-start px-2 py-1 border border-[#C29A5B]/40 bg-[#6B1535]/5 text-[0.6rem] tracking-[0.16em] uppercase hover:bg-[#6B1535]/10 hover:border-[#C29A5B]/70 transition-colors"
                        data-testid={`promo-code-${p.id}`}
                        title="Click to copy"
                    >
                        <span className="text-[#7A5262]">Code</span>
                        <span className="gold-text font-medium">{p.coupon_code}</span>
                        <Copy size={9} className="text-[#7A5262]" />
                    </button>
                )}
                {p.type === "package" && p.package_price != null && !unavailable && (
                    <div className="mt-2 inline-flex items-center gap-1.5 self-start px-2 py-1 border border-[#C29A5B]/40 bg-[#6B1535]/5 text-[0.6rem] tracking-[0.16em] uppercase" data-testid={`promo-package-price-${p.id}`}>
                        <span className="text-[#7A5262]">Package</span>
                        <span className="gold-text font-medium">S${p.package_price}</span>
                    </div>
                )}
                {unavailable ? (
                    <PromoWaitlistForm promoId={p.id} reason={soldOut ? "sold-out" : "ended"} />
                ) : (
                    <>
                        <PromoUrgency seatsLeft={p.seats_left} expiresAt={p.expires_at} testId={`promo-urgency-${p.id}`} />
                        <div className="mt-2 pt-2 border-t border-[#6B1535]/10">
                            <span className="inline-flex items-center gap-1.5 text-[0.65rem] tracking-[0.18em] uppercase text-[#3D0C1F] group-hover:text-[#8F622C] transition-colors" data-testid={`promo-redeem-${p.id}`}>
                                {p.type === "package" ? "Book package" : "Redeem now"} <ArrowUpRight size={10} />
                            </span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function PromoWaitlistForm({ promoId, reason }) {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setSubmitting(true);
        try {
            await api.post(`/promotions/${promoId}/waitlist`, { email: email.trim(), name: name.trim() });
            setDone(true);
            toast.success("You're on the waitlist");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Could not join waitlist"));
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="mt-5 pt-4 border-t border-[#6B1535]/15 text-xs text-[#7A5262]" data-testid={`promo-waitlist-success-${promoId}`}>
                <Sparkles size={12} className="inline text-[#8F622C] mr-1.5" />
                We'll notify you the moment a spot opens.
            </div>
        );
    }

    return (
        <div className="mt-5 pt-4 border-t border-[#6B1535]/15" data-testid={`promo-waitlist-${promoId}`}>
            {!open ? (
                <button
                    onClick={() => setOpen(true)}
                    className="btn-outline-gold w-full justify-center text-xs"
                    data-testid={`promo-waitlist-trigger-${promoId}`}
                >
                    {reason === "sold-out" ? "Sold out — Join Waitlist" : "Offer ended — Notify me next time"} <ArrowUpRight size={12} />
                </button>
            ) : (
                <form onSubmit={submit} className="space-y-2" data-testid={`promo-waitlist-form-${promoId}`}>
                    <input
                        type="email"
                        required
                        placeholder="Your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none"
                        data-testid={`promo-waitlist-email-${promoId}`}
                    />
                    <input
                        type="text"
                        placeholder="Name (optional)"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none"
                        data-testid={`promo-waitlist-name-${promoId}`}
                    />
                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-gold w-full justify-center text-xs disabled:opacity-50"
                        data-testid={`promo-waitlist-submit-${promoId}`}
                    >
                        {submitting ? "Adding…" : "Notify me"} <Send size={10} />
                    </button>
                </form>
            )}
        </div>
    );
}

function PromoUrgency({ seatsLeft, expiresAt, testId }) {
    const [remaining, setRemaining] = useState(() => calcRemaining(expiresAt));

    useEffect(() => {
        if (!expiresAt) return;
        const t = setInterval(() => setRemaining(calcRemaining(expiresAt)), 1000);
        return () => clearInterval(t);
    }, [expiresAt]);

    const hasSeats = typeof seatsLeft === "number" && seatsLeft > 0;
    const hasCountdown = expiresAt && remaining && !remaining.expired;
    const expired = expiresAt && remaining && remaining.expired;

    if (!hasSeats && !hasCountdown && !expired) return null;

    return (
        <div className="mt-5 pt-4 border-t border-[#6B1535]/15 space-y-2" data-testid={testId}>
            {hasSeats && (
                <div className="flex items-center gap-2 text-xs">
                    <Flame size={12} className="text-[#8F622C]" />
                    <span className="text-[#3D0C1F]">
                        Only <span className="font-medium gold-text">{seatsLeft}</span> {seatsLeft === 1 ? "seat" : "seats"} left at this price
                    </span>
                </div>
            )}
            {hasCountdown && (
                <div className="flex items-center gap-2 text-xs">
                    <Clock size={12} className="text-[#8F622C]" />
                    <span className="text-[#7A5262]">Ends in</span>
                    <span className="font-mono text-[#3D0C1F] tracking-wider">
                        {remaining.days > 0 && `${remaining.days}d `}
                        {String(remaining.hours).padStart(2, "0")}:
                        {String(remaining.minutes).padStart(2, "0")}:
                        {String(remaining.seconds).padStart(2, "0")}
                    </span>
                </div>
            )}
            {expired && (
                <div className="text-xs text-[#8A6574] italic">Offer ended</div>
            )}
        </div>
    );
}

function calcRemaining(expiresAt) {
    if (!expiresAt) return null;
    const end = new Date(expiresAt).getTime();
    if (Number.isNaN(end)) return null;
    const diff = end - Date.now();
    if (diff <= 0) return { expired: true };
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);
    return { expired: false, days, hours, minutes, seconds };
}
